import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// Context pour le Toast global
const ToastContext = createContext(null);

// Configuration des types de toast
const TOAST_CONFIG = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-green-500',
    borderColor: 'border-green-600',
    iconBg: 'bg-green-600',
  },
  error: {
    icon: XCircle,
    bgColor: 'bg-red-500',
    borderColor: 'border-red-600',
    iconBg: 'bg-red-600',
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-orange-500',
    borderColor: 'border-orange-600',
    iconBg: 'bg-orange-600',
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-500',
    borderColor: 'border-blue-600',
    iconBg: 'bg-blue-600',
  },
};

// Composant Toast individuel
const ToastItem = ({ toast, onClose }) => {
  const config = TOAST_CONFIG[toast.type] || TOAST_CONFIG.info;
  const Icon = config.icon;

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(toast.id);
    }, toast.duration || 4000);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onClose]);

  return (
    <div
      className={`
        flex items-center gap-4 p-4 rounded-2xl shadow-2xl border-2
        ${config.bgColor} ${config.borderColor}
        text-white min-w-[320px] max-w-[450px]
        animate-slideIn
      `}
      style={{
        animation: 'slideIn 0.3s ease-out',
      }}
    >
      {/* Icône */}
      <div className={`w-12 h-12 rounded-full ${config.iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-7 h-7 text-white" />
      </div>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="font-bold text-lg leading-tight">{toast.title}</p>
        )}
        {toast.message && (
          <p className="text-white/90 text-sm mt-0.5">{toast.message}</p>
        )}
      </div>

      {/* Bouton fermer */}
      <button
        onClick={() => onClose(toast.id)}
        className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors flex-shrink-0"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

// Provider du Toast
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((options) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      type: 'info',
      duration: 4000,
      ...options,
    };
    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Raccourcis pratiques
  const success = useCallback((title, message) => {
    return addToast({ type: 'success', title, message });
  }, [addToast]);

  const error = useCallback((title, message) => {
    return addToast({ type: 'error', title, message, duration: 6000 });
  }, [addToast]);

  const warning = useCallback((title, message) => {
    return addToast({ type: 'warning', title, message });
  }, [addToast]);

  const info = useCallback((title, message) => {
    return addToast({ type: 'info', title, message });
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, success, error, warning, info }}>
      {children}

      {/* Container des toasts - centré en haut */}
      <div className="fixed top-0 left-0 right-0 z-[9999] flex flex-col items-center pt-6 pointer-events-none">
        <div className="flex flex-col gap-3 pointer-events-auto">
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onClose={removeToast} />
          ))}
        </div>
      </div>

      {/* Styles CSS pour l'animation */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </ToastContext.Provider>
  );
};

// Hook pour utiliser le Toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Export par défaut
export default { ToastProvider, useToast };
